import React, { useState } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    useDroppable
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

// Sortable item component
function SortableItem({ step, selectedOptions, isLocked = false, index, collectionId }) {
    const selection = selectedOptions[step.stepName.toLowerCase()];
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ 
        id: step.stepName,
        disabled: isLocked
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        cursor: isLocked ? 'default' : 'grab',
    };

    // Drop Indicator
    const { setNodeRef: setDropRef, isOver } = useDroppable({ id: `drop-${index}` });

    return (
        <>
            <div 
                ref={setNodeRef}
                style={style}
                className={`flex flex-col gap-2 border-2 border-purple cursor-grab p-1 relative touch-none ${
                    isLocked ? 'cursor-default' : ''
                } hover:bg-gray-100`}
                {...(isLocked ? {} : { ...attributes, ...listeners })}
            >
                <span className="font-semibold text-xs text-gray-600">
                    {step.title}:
                </span>
                {selection ? (
                    <div className="flex flex-col items-center gap-1">
                        <img 
                            src={require(`../images/${collectionId}/${step.stepName.toLowerCase()}/${selection.imageName}.png`)}
                            alt={selection.name}
                            title={selection.name}
                            className="w-12 h-12 object-contain"
                        />
                        <span className="text-xs text-gray-800 text-center">{selection.name}</span>
                    </div>
                ) : (
                    <span className="text-xs text-gray-500 italic">Not selected</span>
                )}
            </div>
        </>
    );
}

function SelectedOptions({ selectedOptions, stepsData, onReorder, collectionId }) {
    const [activeId, setActiveId] = useState(null);
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Minimum distance required before drag starts
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        
        if (!active || !over || active.id === over.id) return;

        const oldIndex = stepsData.findIndex(item => item.stepName === active.id);
        const newIndex = stepsData.findIndex(item => item.stepName === over.id);
        
        // Prevent dropping before background or body
        if (newIndex < 1) return;
        
        // Prevent dragging background or body
        if (oldIndex < 1) return;

        const newOrder = arrayMove(stepsData, oldIndex, newIndex).map(item => item.stepName);
        onReorder(newOrder);
    };

    return (
        <div className="mt-8 w-full">
            <p className="text-sm text-center text-black">Reorder by drag/drop</p>
            
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToHorizontalAxis]}
            >
                <SortableContext
                    items={stepsData.map(step => step.stepName)}
                    strategy={horizontalListSortingStrategy}
                >
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-1 relative">
                        {stepsData.map((step, index) => (
                            <SortableItem
                                key={step.stepName}
                                step={step}
                                selectedOptions={selectedOptions}
                                isLocked={index < 1} // Lock background
                                index={index}
                                collectionId={collectionId}
                            />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>

        </div>
    );
}

export default SelectedOptions;
